<template>
  <WidgetCard title="Open site-wide incidents" icon="safety-certificate" :loading="loading" :error="error">
    <!-- Zero open site-wide incidents is the normal, good state. It gets a calm green
         panel, not the grey "no data" empty state, so it never reads as broken. -->
    <div v-if="!incidents.length" class="ce-ok">
      <a-icon class="ce-ok__icon" type="check-circle" theme="filled" />
      <div>
        <p class="ce-ok__title mb-0">Nothing is open</p>
        <p class="ce-ok__sub mb-0">No website is defaced or unreachable right now.</p>
      </div>
    </div>
    <div v-else class="ce-open">
      <!-- The answer first: how many of each kind. The list below repeats it in
           detail, but a glance at this line is meant to be enough. -->
      <p class="ce-open__lead">{{ countLine }}</p>
      <ul class="ce-open__list">
        <!-- Unreachable first, because a site nobody can use beats a defaced
             site. Then worst severity, then the most recent alert. -->
        <li v-for="row in sortedIncidents" :key="row.id" class="ce-open__item">
          <span class="ce-open__dot" :title="row.severityLabel" :style="{ background: severityColor(row.maxSeverity) }" />
          <router-link class="ce-open__link" :to="'/change-events/site-incidents/' + row.id">{{ row.site }}</router-link>
          <span class="ce-open__kind" :style="{ background: kindColor(row.impactType) }">{{ row.impactLabel }}</span>
          <span class="ce-open__count">{{ formatNumber(row.alertCount) }} alerts</span>
        </li>
      </ul>
      <router-link class="ce-open__more" to="/change-events/site-incidents?status=active">See all site-wide incidents</router-link>
    </div>
  </WidgetCard>
</template>

<script>
  import WidgetCard from './WidgetCard.vue';
  import dashboardWidget from '../../mixins/dashboardWidget.mixin';
  import ChangeEventsService from '@modules/ChangeEvents/services/ChangeEvents.service';
  import moment from 'moment-timezone';
  import {
    SEVERITY_COLORS,
    categoryLabel,
    severityColor,
    severityKey,
    severityLabel,
    formatNumber
  } from '@modules/ChangeEvents/consts/ChangeEvents.consts';

  /** How many rows fit before the "see all" link. */
  const MAX_ROWS = 8;

  /**
   * The colour of each category. Both come from the severity palette this
   * section already uses: unreachable takes the HIGH colour because nobody can
   * use the site at all, site-wide defacement takes the MEDIUM one.
   */
  const KIND_COLORS = {
    availability: SEVERITY_COLORS.HIGH,
    defacement: SEVERITY_COLORS.MEDIUM
  };

  /** Unreachable reads before defaced; anything else reads last. */
  const KIND_ORDER = { availability: 0, defacement: 1 };

  /** Worst severity first. */
  const SEVERITY_ORDER = { HIGH: 0, MEDIUM: 1, LOW: 2, unscored: 3 };

  /** Reads a time as milliseconds, or 0 when it is not a time. No zone means UTC. */
  function toMs(value) {
    if (!value) return 0;
    const parsed = moment.utc(String(value).replace(' ', 'T'), moment.ISO_8601);
    return parsed.isValid() ? parsed.valueOf() : 0;
  }

  export default {
    name: 'OpenSiteIncidentsWidget',
    components: { WidgetCard },
    mixins: [dashboardWidget],
    data() {
      return {
        incidents: []
      };
    },
    computed: {
      /**
       * The most urgent first: unreachable before defaced, then worst
       * severity, then the most recent alert.
       */
      sortedIncidents() {
        return this.incidents.slice().sort((a, b) => {
          const kind = this.kindRank(a.impactType) - this.kindRank(b.impactType);
          if (kind !== 0) return kind;
          const severity = SEVERITY_ORDER[a.maxSeverity] - SEVERITY_ORDER[b.maxSeverity];
          if (severity !== 0) return severity;
          return b.lastAlertAt - a.lastAlertAt;
        });
      },
      /** For example "2 unreachable, 1 defaced". */
      countLine() {
        const unreachable = this.incidents.filter((row) => row.impactType === 'availability').length;
        const defaced = this.incidents.filter((row) => row.impactType === 'defacement').length;
        const other = this.incidents.length - unreachable - defaced;
        const parts = [];
        if (unreachable) parts.push(`${unreachable} unreachable`);
        if (defaced) parts.push(`${defaced} defaced`);
        if (other) parts.push(`${other} other`);
        return parts.join(', ');
      }
    },
    methods: {
      severityColor,
      kindColor(impactType) {
        return KIND_COLORS[impactType] || SEVERITY_COLORS.unscored;
      },
      kindRank(impactType) {
        return KIND_ORDER[impactType] === undefined ? 2 : KIND_ORDER[impactType];
      },
      formatNumber,
      fetchResource() {
        return ChangeEventsService.fetchSiteIncidents({ status: 'active', page: 1, perPage: MAX_ROWS });
      },
      applyResult(res) {
        // Strict on purpose. A body that is not { data: [...] } must show the
        // widget's error state, not a green "Nothing is open". The mixin turns
        // this throw into that error state.
        const rows = res && res.data;
        if (!Array.isArray(rows)) throw new Error('Unexpected site incidents response');
        this.incidents = rows.map((item) => ({
          id: item.id,
          site: item.site || '',
          impactType: item.impact_type || '',
          impactLabel: categoryLabel(item),
          maxSeverity: severityKey(item.max_severity),
          severityLabel: severityKey(item.max_severity) === 'unscored' ? 'Not scored' : `${severityLabel(item.max_severity)} severity`,
          lastAlertAt: toMs(item.last_alert_at),
          alertCount: item.alert_count == null ? 0 : item.alert_count
        }));
      }
    }
  };
</script>

<style lang="scss" scoped>
  .ce-ok {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #e6f6e9;
    border: 1px solid #c6e8ce;
    border-radius: 6px;
    padding: 14px 16px;

    &__icon {
      font-size: 22px;
      color: #1e8e3e;
    }
    &__title {
      font-size: 14px;
      font-weight: 600;
      color: #1e8e3e;
    }
    &__sub {
      font-size: 12px;
      color: #5b6570;
    }
  }
  .ce-open {
    display: flex;
    flex-direction: column;
    min-height: 0;
    flex: 1;

    &__lead {
      font-size: 12.5px;
      color: #5b6570;
      margin-bottom: 6px;
    }
    &__list {
      list-style: none;
      padding: 0;
      margin: 0;
      overflow-y: auto;
      min-height: 0;
      flex: 1;
    }
    &__item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 0;
      border-bottom: 1px solid #f0f2f4;
      font-size: 12.5px;
    }
    &__dot {
      width: 9px;
      height: 9px;
      border-radius: 50%;
      flex: none;
    }
    &__link {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: #232a31;

      &:hover {
        color: #20a4b8;
        text-decoration: underline;
      }
    }
    /* The kind, as a small solid chip. It is the fact the customer scans for,
       so it carries colour; the row around it stays quiet. */
    &__kind {
      flex: none;
      color: #ffffff;
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 0.03em;
      padding: 1px 7px;
      border-radius: 9px;
      white-space: nowrap;
    }
    &__count {
      color: #8a94a0;
      white-space: nowrap;
    }
    &__more {
      margin-top: 6px;
      font-size: 12px;
      color: #20a4b8;

      &:hover {
        text-decoration: underline;
      }
    }
  }
</style>
